import { createContext, ReactNode, useContext } from 'react';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import {
  useContactFormData,
  UseContactFormDataReturn,
} from 'src/SharedCustomHooks/UseContactFormData/useContactFormData';
import { useLocationSearch } from 'src/SharedCustomHooks/UseLocationSearch/useLocationSearch';
import {
  ParsedAddress,
  UseLocationSearchReturn,
} from 'src/SharedCustomHooks/UseLocationSearch/types';

interface ContactFormProviderProps {
  children: ReactNode;
  initialData?: Partial<IContactFormData>;
}

type ContactFormDataKeys = 'contactFormData' | 'isLoading' | 'errors';
type LocationSearchDataKeys = 'location' | 'predictions';

export type ContactFormDataContext = Pick<
  UseContactFormDataReturn,
  ContactFormDataKeys
> &
  Pick<UseLocationSearchReturn, LocationSearchDataKeys>;

export type ContactFormActionsContext = Omit<
  UseContactFormDataReturn,
  ContactFormDataKeys
> &
  Omit<UseLocationSearchReturn, LocationSearchDataKeys>;

const DataContext = createContext<ContactFormDataContext | undefined>(
  undefined
);
const ActionsContext = createContext<ContactFormActionsContext | undefined>(
  undefined
);

export const ContactFormProvider = ({ children }: ContactFormProviderProps) => {
  const {
    contactFormData,
    isLoading,
    errors,
    ...contactFormActions
  }: UseContactFormDataReturn = useContactFormData();

  const {
    location,
    predictions,
    ...locationSearchActions
  }: UseLocationSearchReturn = useLocationSearch({
    onAddressSelected: (address: ParsedAddress) =>
      contactFormActions.setContactFormData({ address }),
  });

  const dataValue: ContactFormDataContext = {
    contactFormData,
    isLoading,
    errors,
    location,
    predictions,
  };

  const actionsValue: ContactFormActionsContext = {
    ...contactFormActions,
    ...locationSearchActions,
  };

  return (
    <ActionsContext.Provider value={actionsValue}>
      <DataContext.Provider value={dataValue}>{children}</DataContext.Provider>
    </ActionsContext.Provider>
  );
};

export const useContactFormDataFromContext = (): ContactFormDataContext => {
  const context = useContext(DataContext);

  if (context === undefined) {
    throw new Error(
      'useContactFormDataFromContext must be used within a ContactFormProvider'
    );
  }

  return context;
};

export const useContactFormActionsFromContext =
  (): ContactFormActionsContext => {
    const context = useContext(ActionsContext);

    if (context === undefined) {
      throw new Error(
        'useContactFormActionsFromContext must be used within a ContactFormProvider'
      );
    }

    return context;
  };